/**
 * Iter 250 · Drag-and-drop logo + icon uploader for Workspace Settings.
 *
 * Uses the new /workspaces/{ws}/branding/upload endpoints (Iter 249).
 * Renders two side-by-side drop zones (Logo + Icon), 5 MB each,
 * PNG/JPG/SVG/WEBP allowed. Shows a live preview streamed from the
 * public GET /workspaces/{ws}/branding/{kind} endpoint.
 */
import { useState, useCallback, useRef, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, Trash2, Loader2 } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/webp"];

function DropZone({ wsId, kind, label, hint, dimensions }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewToken, setPreviewToken] = useState(Date.now()); // cache-buster
  const [hasFile, setHasFile] = useState(false);
  const inputRef = useRef(null);

  const previewUrl = `${api.defaults.baseURL || ""}/workspaces/${wsId}/branding/${kind}?v=${previewToken}`;

  const checkExists = useCallback(async () => {
    try {
      const res = await fetch(previewUrl, { method: "HEAD" });
      setHasFile(res.ok);
    } catch { setHasFile(false); }
  }, [previewUrl]);

  useEffect(() => { if (wsId) checkExists(); }, [wsId, checkExists]);

  const upload = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error(`Unsupported: ${file.type} (allowed: PNG, JPG, SVG, WEBP)`); return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Too large: ${(file.size / (1024 * 1024)).toFixed(1)} MB (max 5 MB)`); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/workspaces/${wsId}/branding/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${label} uploaded`);
      setPreviewToken(Date.now());
      setHasFile(true);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
    } finally { setUploading(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove the current ${label.toLowerCase()}?`)) return;
    try {
      await api.delete(`/workspaces/${wsId}/branding/${kind}`);
      toast.success(`${label} removed`);
      setHasFile(false);
      setPreviewToken(Date.now());
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) upload(f);
  };

  return (
    <div className="flex-1 min-w-[260px]" data-testid={`logo-uploader-${kind}`}>
      <div className="label-eyebrow mb-2 flex items-center justify-between">
        <span>{label}</span>
        {hasFile && (
          <button type="button" onClick={remove}
                   className="normal-case tracking-normal text-[11px] text-muted-foreground hover:text-rose-600 flex items-center gap-1"
                   data-testid={`logo-remove-${kind}`}>
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
      </div>
      <label
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        htmlFor={`logo-input-${kind}`}
        className={`block border-2 border-dashed cursor-pointer transition-colors ${dragging ? "border-primary bg-primary/5" : hasFile ? "border-border bg-muted/20" : "border-border hover:border-primary/60 bg-muted/10"} p-4 text-center`}
      >
        <input
          ref={inputRef}
          id={`logo-input-${kind}`}
          type="file"
          accept={ALLOWED.join(",")}
          className="sr-only"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
          data-testid={`logo-input-${kind}`}
        />
        {hasFile ? (
          <div className="flex flex-col items-center gap-2">
            <img
              src={previewUrl}
              alt={`${label} preview`}
              className={`bg-white ${kind === "icon" ? "w-16 h-16 object-contain" : "max-h-16 object-contain"}`}
              onError={() => setHasFile(false)}
              data-testid={`logo-preview-${kind}`}
            />
            <div className="text-[11px] text-muted-foreground">Drop a new file to replace</div>
          </div>
        ) : uploading ? (
          <div className="flex flex-col items-center gap-2 py-4">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <div className="text-xs text-muted-foreground">Uploading…</div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-4">
            <div className={`${kind === "icon" ? "w-10 h-10" : "w-12 h-8"} bg-muted flex items-center justify-center text-muted-foreground border border-border`}>
              <ImageIcon className="w-4 h-4" />
            </div>
            <div className="text-sm font-medium">
              <Upload className="w-3.5 h-3.5 inline mr-1" /> Drop or click to upload
            </div>
            <div className="text-[11px] text-muted-foreground">{dimensions}</div>
          </div>
        )}
      </label>
      <p className="text-[11px] text-muted-foreground mt-1.5">{hint}</p>
    </div>
  );
}

export default function LogoUploader({ wsId }) {
  if (!wsId) return null;
  return (
    <div className="border border-border p-6 space-y-4" data-testid="logo-uploader-section">
      <div className="flex items-center gap-2">
        <ImageIcon className="w-4 h-4 text-primary" />
        <div>
          <div className="label-eyebrow">Workspace logo &amp; icon</div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Uploaded assets appear in the sidebar, login page, and the public client RFQ portal automatically.
            No CDN required — files are stored in our object storage.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-4">
        <DropZone
          wsId={wsId}
          kind="logo"
          label="Full logo"
          hint="Wide horizontal logo used on the login page, sidebar (expanded), and portal header."
          dimensions="Recommended: 240×64 · PNG/JPG/SVG/WEBP · ≤5 MB"
        />
        <DropZone
          wsId={wsId}
          kind="icon"
          label="Square icon"
          hint="Square favicon-style icon used in the collapsed sidebar, browser tabs, and notifications."
          dimensions="Recommended: 64×64 · PNG/JPG/SVG/WEBP · ≤5 MB"
        />
      </div>
    </div>
  );
}
